'use client';

import clsx from 'clsx';
import { Link } from '@/components/link/Link';
import { NavLinkList } from './NavLinkList';
import { NavSectionButton } from './NavSectionButton';
import {
  hasSubpanel,
  navKey,
  navPanelId,
  navSubpanelId,
  navTriggerId,
} from '../nav-data';
import type { NavGroup } from '../types';

type NavPanelProps = {
  group: NavGroup;
  /** Identity of the group, unique even if two groups share a title. */
  groupKey: string;
  isActive: boolean;
  openColumnItem: string | null;
  onToggleColumnItem: (itemKey: string) => void;
  onNavigate: () => void;
};

const columnItemClassName =
  'smzh-link flex w-full items-center justify-between gap-[24px] rounded px-3 py-1 text-left text-neutral-400 hover:text-neutral-700 active:bg-brand-100';

/**
 * One mega-menu panel.
 *
 * The panel is rendered on the server for every group and merely hidden while
 * another group is open, instead of being mounted on first click. A link that
 * only exists after an interaction is invisible to a crawler and to anything
 * reading the page without a pointer, which is what stranded the bulk of the
 * site behind this menu (WB-813). Everything a visitor can reach by opening
 * the panel is in the markup they were served.
 */
export const NavPanel = ({
  group,
  groupKey,
  isActive,
  openColumnItem,
  onToggleColumnItem,
  onNavigate,
}: NavPanelProps) => {
  // Keys come from the unfiltered position so they stay stable no matter which
  // sections turn out to be links rather than triggers.
  const sections = (group.items ?? []).map((item, index) => ({
    item,
    key: navKey(item.title, index),
  }));

  if (!sections.length) return null;

  return (
    <div
      id={navPanelId(groupKey)}
      aria-labelledby={navTriggerId(groupKey)}
      hidden={!isActive}
      className={clsx(
        'relative w-full max-w-[1064px] rounded-[8px] border',
        isActive ? 'flex' : 'hidden',
      )}
    >
      {/* Left column: the sections of this group */}
      <div className="relative z-10 flex w-[40%] flex-col gap-[14px] rounded-l-[8px] bg-white px-[18px] py-[24px]">
        {sections.map(({ item, key }) => {
          if (item.slug) {
            return (
              <Link
                key={key}
                href={item.slug}
                className="group/sublinks !flex !w-full !items-center !justify-between !gap-[24px] !rounded !px-3 !py-1 !text-neutral-400 hover:!text-neutral-700 hover:!no-underline active:!bg-brand-100"
                onClick={onNavigate}
              >
                {item.title}
              </Link>
            );
          }

          // Neither a destination nor anything to reveal: nothing to render.
          if (!hasSubpanel(item)) return null;

          const isOpen = openColumnItem === key;

          return (
            <NavSectionButton
              key={key}
              title={item.title}
              isOpen={isOpen}
              controlsId={navSubpanelId(groupKey, key)}
              onToggle={() => onToggleColumnItem(key)}
              className={columnItemClassName}
            />
          );
        })}
      </div>

      {/* Right column: the default sections, or the selected section's links */}
      <div className="dropdown-nav-right z-20 flex h-full w-2/3 flex-col justify-start rounded-r-[8px] border-l border-neutral-50 bg-neutral-25 px-[18px] py-[24px] transition-all">
        <div
          hidden={Boolean(openColumnItem)}
          className={clsx('flex-col gap-9', openColumnItem ? 'hidden' : 'flex')}
        >
          {(group.defaultNavbarItem ?? []).map((section, index) => (
            <div key={navKey(section.title, index)} className="flex flex-col">
              <p className="mb-4 text-xs font-medium uppercase text-gray-400">
                {section.title}
              </p>
              <NavLinkList links={section.items} onNavigate={onNavigate} />
            </div>
          ))}
        </div>

        {/* Only the sections that own a trigger get a sub-panel; a section
            that is itself a link has nothing to reveal. */}
        {sections
          .filter(({ item }) => hasSubpanel(item))
          .map(({ item, key }) => {
            const isOpen = openColumnItem === key;

            return (
              <div
                key={key}
                id={navSubpanelId(groupKey, key)}
                hidden={!isOpen}
                className={clsx(isOpen ? 'block' : 'hidden')}
              >
                <p className="mb-4 text-xs font-medium uppercase text-gray-400">
                  {item.title}
                </p>
                <NavLinkList
                  links={item.clickedItems}
                  onNavigate={onNavigate}
                />
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default NavPanel;
